/* Shop admin panel — extracted from Admin.jsx tech-debt split. */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sparkle, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MAX_UPLOAD_MB = 10;
const ACCEPT_MEDIA = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime";

export default function ShopPanel() {
  const [shop, setShop] = useState([]);
  const [editingShop, setEditingShop] = useState(null);
  const [uploadingShop, setUploadingShop] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const shopFileRef = useRef(null);

  const refresh = () => api.get("/shop").then((r) => setShop(r.data));
  const refreshAnalytics = (days = analyticsDays) =>
    api.get(`/admin/shop-analytics?days=${days}`).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
  useEffect(() => { refresh(); refreshAnalytics(analyticsDays); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadShopImage = async (file) => {
    if (!file || !editingShop) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`File is too large — please keep uploads under ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setUploadingShop(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setEditingShop({ ...editingShop, image_url: data.url });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingShop(false); }
  };

  const saveShop = async () => {
    try {
      if (editingShop.item_id) {
        await api.patch(`/shop/${editingShop.item_id}`, editingShop);
      } else {
        await api.post("/shop", editingShop);
      }
      toast.success("Saved");
      setEditingShop(null);
      refresh();
    } catch { toast.error("Save failed"); }
  };

  const deleteShop = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    await api.delete(`/shop/${item.item_id}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div>
      {/* Shop analytics — top chips, top items, and chip→item conversions from
          /api/shop/track events. Helps admins see which "Popular" searches convert. */}
      <div className="card-recipe mb-6" data-testid="shop-analytics">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="overline">Shop analytics</p>
            <h3 className="font-display text-xl font-black text-rf-ink">Search & click activity</h3>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                data-testid={`shop-analytics-range-${d}`}
                onClick={() => { setAnalyticsDays(d); refreshAnalytics(d); }}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${analyticsDays === d ? "bg-rf-ink text-rf-cream border-rf-ink" : "bg-white text-rf-ink-soft border-rf-ink/10 hover:border-rf-orange"}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {analytics ? (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <div className="rounded-xl bg-rf-cream-alt p-4">
                <p className="text-xs text-rf-ink-soft">Chip clicks (last {analytics.days}d)</p>
                <p className="font-display text-3xl font-black text-rf-ink" data-testid="shop-analytics-chip-total">{analytics.total_chip_clicks}</p>
              </div>
              <div className="rounded-xl bg-rf-cream-alt p-4">
                <p className="text-xs text-rf-ink-soft">Outbound item clicks</p>
                <p className="font-display text-3xl font-black text-rf-ink" data-testid="shop-analytics-item-total">{analytics.total_item_clicks}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="overline mb-2">Top chips</p>
                {analytics.top_chips.length === 0 ? (
                  <p className="text-xs text-rf-ink-soft">No chip clicks yet.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="shop-analytics-top-chips">
                    {analytics.top_chips.map((c) => (
                      <li key={c.chip} className="flex items-center justify-between text-sm">
                        <span className="font-semibold text-rf-ink">{c.chip}</span>
                        <span className="tabular-nums text-rf-ink-soft">{c.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="overline mb-2">Top clicked items</p>
                {analytics.top_items.length === 0 ? (
                  <p className="text-xs text-rf-ink-soft">No outbound clicks yet.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="shop-analytics-top-items">
                    {analytics.top_items.map((it) => (
                      <li key={it.item_id} className="flex items-center justify-between text-sm gap-3">
                        <span className="font-semibold text-rf-ink truncate">{it.title}</span>
                        <span className="tabular-nums text-rf-ink-soft shrink-0">{it.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {analytics.conversions.length > 0 && (
              <div className="mt-6">
                <p className="overline mb-2">Chip → item conversions</p>
                <ul className="space-y-1.5" data-testid="shop-analytics-conversions">
                  {analytics.conversions.map((c) => (
                    <li key={`${c.chip}-${c.item_id}`} className="flex items-center justify-between text-sm gap-3">
                      <span className="text-rf-ink truncate">
                        <span className="chip !py-0.5 !px-2 mr-2">{c.chip}</span>
                        {c.title}
                      </span>
                      <span className="tabular-nums text-rf-ink-soft shrink-0">{c.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-rf-ink-soft">Loading analytics…</p>
        )}
      </div>

      <div className="flex justify-end mb-4">
        <button data-testid="shop-new" onClick={() => setEditingShop({ category: "food_drink", title: "", summary: "", image_url: "", external_url: "", on_sale: false, sale_text: "" })} className="btn-amber"><Sparkle size={16} /> New shop item</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shop.map((s) => (
          <div key={s.item_id} className="card-recipe">
            {s.image_url && <img src={s.image_url} alt="" className="w-full aspect-square object-cover rounded-lg mb-3" />}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="chip">{s.category === "food_drink" ? "Food & Drink" : "Tools"}</span>
              {s.on_sale && <span className="chip !bg-rf-orange/20 !text-rf-orange">{s.sale_text || "Sale"}</span>}
            </div>
            <p className="font-bold text-rf-ink line-clamp-2">{s.title}</p>
            <p className="text-xs text-rf-ink-soft line-clamp-2 mt-1 mb-3">{s.summary}</p>
            <div className="flex gap-2">
              <button data-testid={`shop-edit-${s.item_id}`} onClick={() => setEditingShop({ ...s })} className="btn-outline-ink !py-2 !px-3 !text-xs"><PencilSimple size={14} /> Edit</button>
              <button onClick={() => deleteShop(s)} className="text-red-600 inline-flex items-center gap-1 text-xs font-bold ml-auto"><Trash size={14} /> Delete</button>
            </div>
          </div>
        ))}
        {shop.length === 0 && <p className="text-rf-ink-soft text-center col-span-full py-10">No shop items yet.</p>}
      </div>

      <Dialog open={!!editingShop} onOpenChange={(o) => !o && setEditingShop(null)}>
        <DialogContent className="bg-rf-cream border-rf-ink/10 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black text-rf-ink">{editingShop?.item_id ? "Edit shop item" : "New shop item"}</DialogTitle></DialogHeader>
          {editingShop && (
            <div className="space-y-4">
              <div>
                <label className="overline">Category</label>
                <select value={editingShop.category} onChange={(e) => setEditingShop({ ...editingShop, category: e.target.value })} className="input-rf mt-1">
                  <option value="food_drink">Food & Drink</option>
                  <option value="tools">Tools</option>
                </select>
              </div>
              <div>
                <label className="overline">Title</label>
                <input data-testid="shop-edit-title" value={editingShop.title} onChange={(e) => setEditingShop({ ...editingShop, title: e.target.value })} className="input-rf mt-1" />
              </div>
              <div>
                <label className="overline">Summary</label>
                <textarea rows={2} value={editingShop.summary} onChange={(e) => setEditingShop({ ...editingShop, summary: e.target.value })} className="input-rf mt-1" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="overline">Image</label>
                  <button data-testid="shop-edit-upload" onClick={() => shopFileRef.current?.click()} disabled={uploadingShop} className="text-rf-orange text-xs font-bold inline-flex items-center gap-1"><UploadSimple size={14} /> {uploadingShop ? "Uploading…" : (editingShop.image_url ? "Replace" : "Upload")}</button>
                  <input ref={shopFileRef} type="file" accept={ACCEPT_MEDIA} className="hidden" onChange={(e) => uploadShopImage(e.target.files?.[0])} />
                </div>
                {editingShop.image_url && <img src={editingShop.image_url} alt="" className="w-full aspect-square object-cover rounded-xl" />}
              </div>
              <div>
                <label className="overline">External / affiliate URL</label>
                <input value={editingShop.external_url} onChange={(e) => setEditingShop({ ...editingShop, external_url: e.target.value })} className="input-rf mt-1" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!editingShop.on_sale} onChange={(e) => setEditingShop({ ...editingShop, on_sale: e.target.checked })} className="w-5 h-5 accent-rf-orange" />
                <span className="font-display font-bold text-rf-ink">On sale</span>
              </label>
              {editingShop.on_sale && (
                <div>
                  <label className="overline">Sale tag text</label>
                  <input value={editingShop.sale_text || ""} onChange={(e) => setEditingShop({ ...editingShop, sale_text: e.target.value })} placeholder="20% off this week" className="input-rf mt-1" />
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setEditingShop(null)} className="btn-outline-ink">Cancel</button>
                <button data-testid="shop-edit-save" onClick={saveShop} className="btn-amber">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
